import type { RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from '@ionic/vue-router';
import { useAuthStore } from '@/store/auth.store';
import { ROUTE_NAMES } from '@/constants/routes.const';

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/events' },

  // ---- Auth flow (public) ----
  {
    path: '/login',
    name: ROUTE_NAMES.LOGIN,
    component: () => import('@/views/auth/LoginPage.vue'),
    meta: { requiresAuth: false, hideForAuthed: true },
  },
  {
    path: '/register',
    name: ROUTE_NAMES.REGISTER,
    component: () => import('@/views/auth/RegisterPage.vue'),
    meta: { requiresAuth: false, hideForAuthed: true },
  },
  {
    path: '/privacy',
    name: ROUTE_NAMES.PRIVACY,
    component: () => import('@/views/legal/PrivacyPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/terms',
    name: ROUTE_NAMES.TERMS,
    component: () => import('@/views/legal/TermsPage.vue'),
    meta: { requiresAuth: false },
  },

  // ---- Tab navigation shell ----
  {
    path: '/',
    component: () => import('@/views/TabsPage.vue'),
    children: [
      {
        path: 'events',
        name: ROUTE_NAMES.EVENTS_LIST,
        component: () => import('@/views/events/EventsListPage.vue'),
        meta: { requiresAuth: false, guestAllowed: true },
      },
      {
        path: 'people',
        name: ROUTE_NAMES.PEOPLE_LIST,
        component: () => import('@/views/people/PeopleListPage.vue'),
        meta: { requiresAuth: false, guestAllowed: true },
      },
      {
        path: 'favorites',
        name: ROUTE_NAMES.FAVORITES,
        component: () => import('@/views/FavoritesPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'networking',
        name: ROUTE_NAMES.NETWORKING,
        component: () => import('@/views/networking/NetworkingPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'profile',
        name: ROUTE_NAMES.PROFILE,
        component: () => import('@/views/ProfilePage.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },

  // ---- Protected / push routes (outside tabs, full-screen) ----
  {
    path: '/events/:id',
    name: ROUTE_NAMES.EVENT_DETAIL,
    component: () => import('@/views/events/EventDetailPage.vue'),
    props: true,
    meta: { requiresAuth: false, guestAllowed: true },
  },
  {
    path: '/events/:id/agenda',
    name: ROUTE_NAMES.AGENDA,
    component: () => import('@/views/events/AgendaPage.vue'),
    props: true,
    meta: { requiresAuth: false, guestAllowed: true },
  },
  {
    path: '/people/:id',
    name: ROUTE_NAMES.PERSON_DETAIL,
    component: () => import('@/views/people/PersonDetailPage.vue'),
    props: true,
    meta: { requiresAuth: false, guestAllowed: true },
  },
  {
    path: '/admin',
    name: ROUTE_NAMES.ADMIN,
    component: () => import('@/views/admin/AdminPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/profile/edit',
    name: ROUTE_NAMES.PROFILE_EDIT,
    component: () => import('@/views/ProfileEditPage.vue'),
    meta: { requiresAuth: true },
  },

  // ---- Modal-style route (QR scanner) ----
  {
    path: '/events/:id/scan',
    name: ROUTE_NAMES.QR_SCAN,
    component: () => import('@/views/events/QrScanPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/networking/scan',
    name: ROUTE_NAMES.NETWORK_SCAN,
    component: () => import('@/views/networking/NetworkScanPage.vue'),
    meta: { requiresAuth: true },
  },
  { path: '/:pathMatch(.*)*', redirect: '/events' },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();

  if (!authStore.isInitialized) {
    await authStore.restoreSession();
  }

  const sessionActive = await authStore.validateInactivity();
  if (!sessionActive && to.meta.requiresAuth && to.name !== ROUTE_NAMES.LOGIN) {
    return { name: ROUTE_NAMES.LOGIN, query: { expired: '1', redirect: to.fullPath } };
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return {
      name: ROUTE_NAMES.LOGIN,
      query: {
        redirect: to.fullPath,
        restricted: authStore.hasActiveGuestSession ? '1' : undefined,
      },
    };
  }

  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    return { name: ROUTE_NAMES.EVENTS_LIST };
  }

  if (to.meta.hideForAuthed && authStore.isAuthenticated) {
    return { name: authStore.isAdmin ? ROUTE_NAMES.ADMIN : ROUTE_NAMES.EVENTS_LIST };
  }

  return true;
});

export default router;
